/**
 * Company → Web → Domains (ADR-024 rev 4/5): the domains Fias manages for a
 * Company's portals. Every portal lives on one of these — Fias hosts the DNS,
 * so a portal's address can never be pointed somewhere else behind Fias's
 * back. Four ways in:
 *
 *   - Buy a new domain: Fias registers it for the Company (the registrar's
 *     price + 20%, charged in credits to the Company's payer when bought, and
 *     each year before it renews);
 *   - Move a domain you own: the Company transfers an approved domain to Fias.
 *     Fias first copies what its DNS shows today so the owner can check it,
 *     then the owner gives the transfer code (charged then);
 *   - Point your name servers to Fias: a whole approved domain stays at the
 *     Company's registrar; Fias hosts its DNS once the Company sets the name
 *     servers shown. Its security (and renewal) is the Company's registrar
 *     account's, so it is offered after the transfer;
 *   - Use a subdomain: the Company delegates e.g. `portal.yourcompany.com` to
 *     Fias by adding the NS records shown, keeping the rest of its DNS.
 *
 * Every whole domain gets a records editor for everything that is not a
 * portal (mail, verification records, …). Owners change; admins see. The
 * server enforces every rule; this is the affordance.
 */

import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/use-toast';
import {
  api,
  ApiError,
  type CompanyDomainRegistrantContactDto,
  type CompanyWebDelegationCheckDto,
  type CompanyWebDelegationCheckError,
  type CompanyWebDnsRecordDto,
  type CompanyWebDnsRecordType,
  type CompanyWebDomainDto,
  type CompanyWebDomainQuoteDto,
  type CompanyWebDto,
} from '@/lib/api';
import { formatCreditsWithDollar } from '@/lib/format-credits';
import { isRegistrarCountryCode, registrarCountryOptions } from '@/lib/registrar-country-codes';
import { REGISTRAR_PHONE_PATTERN, normalizeRegistrarPhone } from '@/lib/registrar-phone';
import { queryKeys } from '@/lib/query-keys';

const SELECT_CLASS =
  'h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring';

const MODE_LABEL: Record<CompanyWebDomainDto['mode'], string> = {
  registered: 'Bought through Fias',
  transferred: 'Moved to Fias',
  delegated: 'Subdomain delegated to Fias',
  nameservers: 'Name servers pointed at Fias',
};

const RECORD_TYPES: CompanyWebDnsRecordType[] = ['A', 'AAAA', 'CNAME', 'MX', 'TXT', 'SRV', 'CAA'];

function errorText(err: unknown): string {
  if (err instanceof ApiError) return err.message.replace(/^[A-Z_]+:\s*/, '');
  return 'Something went wrong. Please try again.';
}

/**
 * The new price a COMPANY_WEB_DOMAIN_PRICE_CHANGED refusal carries: the
 * registrar's price rose past what the owner was shown, so nothing was
 * charged. Null for any other error.
 */
function risenPrice(err: unknown): number | null {
  if (!(err instanceof ApiError) || err.code !== 'COMPANY_WEB_DOMAIN_PRICE_CHANGED') return null;
  const price = (err.body as { error?: { details?: { priceCredits?: unknown } } } | undefined)
    ?.error?.details?.priceCredits;
  return typeof price === 'number' && Number.isInteger(price) && price > 0 ? price : null;
}

function copy(text: string) {
  void navigator.clipboard?.writeText(text).then(
    () => toast({ title: 'Copied' }),
    () => undefined,
  );
}

/** One line saying where a domain is and what, if anything, the owner must do. */
export function describeDomain(domain: CompanyWebDomainDto): {
  badge: string;
  tone: 'ok' | 'wait' | 'bad';
  detail: string | null;
} {
  switch (domain.status) {
    case 'pending_registration':
      return {
        badge: 'Registering',
        tone: 'wait',
        detail: 'Fias is registering this domain. It usually takes a few minutes.',
      };
    case 'pending_review':
      return {
        badge: 'Check your records',
        tone: 'wait',
        detail:
          'Fias copied what this domain’s DNS shows today. Check the records below, then give the transfer code from your current registrar.',
      };
    case 'pending_transfer':
      return {
        badge: 'Transferring',
        tone: 'wait',
        detail:
          'The transfer is under way. Your current registrar may email you to approve it; it can take up to a few days.',
      };
    case 'pending_delegation':
      if (domain.mode === 'nameservers') {
        return {
          badge: 'Waiting for name servers',
          tone: 'wait',
          detail:
            'Fias copied what this domain’s DNS shows today: check the records below first. Then, at your registrar, replace the domain’s name servers with the ones below. It can take a few hours after that.',
        };
      }
      return {
        badge: 'Waiting for DNS',
        tone: 'wait',
        detail:
          domain.delegationStatus === 'mismatch'
            ? 'NS records for this name point somewhere else. They must be exactly the ones below.'
            : 'Add the NS records below where your domain’s DNS is managed. It can take a few minutes after that.',
      };
    case 'active':
      if (domain.mode !== 'registered' && domain.mode !== 'transferred') {
        if (domain.delegationStatus === 'mismatch' || domain.delegationStatus === 'not_delegated') {
          return {
            badge: domain.mode === 'nameservers' ? 'Name servers changed' : 'Delegation changed',
            tone: 'bad',
            detail:
              domain.mode === 'nameservers'
                ? 'This domain no longer uses Fias’s name servers, so its portals are stopped. Set the name servers below back at your registrar.'
                : 'This name is no longer delegated to Fias, so its portals are stopped. Put the NS records below back.',
          };
        }
      }
      if (domain.mode === 'nameservers' && expiresWithin(domain, 30)) {
        return {
          badge: 'Expires soon',
          tone: 'bad',
          detail: `This domain expires on ${new Date(domain.expiresAt!).toLocaleDateString()}. Renew it at your registrar — Fias cannot — or its portals stop.`,
        };
      }
      return { badge: 'Live', tone: 'ok', detail: null };
    case 'failed':
      return {
        badge: 'Needs attention',
        tone: 'bad',
        detail: domain.lastError ?? 'Fias could not set this domain up. Contact Fias support.',
      };
    case 'removing':
      return {
        badge: 'Removing',
        tone: 'wait',
        detail:
          domain.mode === 'nameservers'
            ? 'Its portals are off. Point its name servers away from Fias at your registrar to finish.'
            : domain.mode === 'delegated'
              ? 'Its portals are off. Remove the NS records for this name from your DNS to finish.'
              : 'Its portals are off. The domain is still yours: transfer it to another registrar to finish.',
      };
    default:
      return { badge: domain.status, tone: 'wait', detail: null };
  }
}

/** Whether a domain's known expiry is within `days` (never true when unknown). */
function expiresWithin(domain: CompanyWebDomainDto, days: number): boolean {
  if (!domain.expiresAt) return false;
  return new Date(domain.expiresAt).getTime() - Date.now() < days * 24 * 3600 * 1000;
}

export function CompanyWebDomains({
  companyId,
  data,
  isOwner,
  onChanged,
}: {
  companyId: string;
  data: CompanyWebDto;
  isOwner: boolean;
  onChanged: () => void;
}) {
  // Domains being removed do not hold a place (the API counts the same way):
  // a replacement can be added while a zone waits for the old DNS to let go.
  const full = data.domains.filter((d) => d.status !== 'removing').length >= data.maxDomains;
  return (
    <div className="space-y-4">
      {data.domains.map((domain) => (
        <DomainCard
          key={domain.domainId}
          companyId={companyId}
          domain={domain}
          isOwner={isOwner}
          onChanged={onChanged}
        />
      ))}
      {isOwner &&
        (data.maxDomains === 0 ? (
          // A grant that allows no domains yet (set to 0 by an admin, or made
          // before domains had a limit). Said so: an empty section under a
          // Portals card that says "add a domain above" explained nothing.
          <p
            className="text-sm text-muted-foreground"
            data-testid="company-web-no-domain-allowance"
          >
            Your Company&apos;s web access does not include any domains yet. Ask Fias to allow one,
            then add it here.
          </p>
        ) : full ? (
          <p className="text-sm text-muted-foreground">
            Your Company has {data.maxDomains === 1 ? 'its domain' : `${data.maxDomains} domains`},
            the most it can have. Contact Fias for more.
          </p>
        ) : (
          <AddDomain companyId={companyId} data={data} onAdded={onChanged} />
        ))}
    </div>
  );
}

function StatusBadge({ tone, children }: { tone: 'ok' | 'wait' | 'bad'; children: string }) {
  return (
    <Badge variant={tone === 'bad' ? 'destructive' : tone === 'ok' ? 'default' : 'secondary'}>
      {children}
    </Badge>
  );
}

function DomainCard({
  companyId,
  domain,
  isOwner,
  onChanged,
}: {
  companyId: string;
  domain: CompanyWebDomainDto;
  isOwner: boolean;
  onChanged: () => void;
}) {
  const state = describeDomain(domain);
  // Imported records are for the owner to check before anything switches.
  const [showRecords, setShowRecords] = useState(
    domain.status === 'pending_review' ||
      (domain.mode === 'nameservers' && domain.status === 'pending_delegation'),
  );
  const [removing, setRemoving] = useState(false);
  const [transferringOut, setTransferringOut] = useState(false);
  const [outCode, setOutCode] = useState<string | null>(null);
  // The last Verify result: what the parent zone showed for each name server.
  const [check, setCheck] = useState<CompanyWebDelegationCheckDto | null>(null);
  // A result describes the domain as it was: once the reconciler moves the
  // domain (live, or drifted again later) the old verdict would be shown under
  // a badge that contradicts it.
  useEffect(() => setCheck(null), [domain.status, domain.delegationStatus]);
  const pointedByCompany = domain.mode === 'delegated' || domain.mode === 'nameservers';
  const showNs =
    pointedByCompany &&
    domain.nameServers.length > 0 &&
    (domain.status === 'pending_delegation' || domain.delegationStatus !== 'delegated');
  // While removing, the records are shown for what to take away; a check then
  // would tell the owner to put them back (the server refuses it too).
  const verifiable =
    pointedByCompany && (domain.status === 'pending_delegation' || domain.status === 'active');
  // Fias holds the registration: renewal and transfer-out are Fias's to offer.
  const registeredHere = domain.mode === 'registered' || domain.mode === 'transferred';

  const autoRenew = useMutation({
    mutationFn: (on: boolean) => api.setCompanyWebDomainAutoRenew(companyId, domain.domainId, on),
    onSuccess: onChanged,
    onError: (err) =>
      toast({
        title: 'Could not change renewal',
        description: errorText(err),
        variant: 'destructive',
      }),
  });
  const confirmRenewal = useMutation({
    mutationFn: () => api.confirmCompanyWebDomainRenewalQuote(companyId, domain.domainId),
    onSuccess: onChanged,
    onError: (err) =>
      toast({
        title: 'Could not confirm the renewal price',
        description: errorText(err),
        variant: 'destructive',
      }),
  });
  const verify = useMutation({
    mutationFn: () => api.checkCompanyWebDomainDelegation(companyId, domain.domainId),
    onSuccess: (res) => {
      setCheck(res.check);
      // The DNS is right: the reconciler moves the domain within minutes, so
      // start following it now rather than at the next scheduled poll.
      if (res.check.status === 'delegated') onChanged();
    },
    onError: (err) =>
      toast({ title: 'Could not verify', description: errorText(err), variant: 'destructive' }),
  });
  const remove = useMutation({
    mutationFn: () => api.removeCompanyWebDomain(companyId, domain.domainId),
    onSuccess: () => {
      setRemoving(false);
      onChanged();
    },
    onError: (err) =>
      toast({ title: 'Could not remove', description: errorText(err), variant: 'destructive' }),
  });
  const transferOut = useMutation({
    mutationFn: () => api.transferOutCompanyWebDomain(companyId, domain.domainId),
    onSuccess: (res) => {
      setTransferringOut(false);
      setOutCode(res.authCode);
      onChanged();
    },
    onError: (err) =>
      toast({
        title: 'Could not unlock the domain',
        description: errorText(err),
        variant: 'destructive',
      }),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2">
          {domain.zoneName}
          <StatusBadge tone={state.tone}>{state.badge}</StatusBadge>
        </CardTitle>
        <CardDescription className="flex flex-wrap items-center gap-2">
          <span>{MODE_LABEL[domain.mode]}</span>
          {registeredHere && domain.expiresAt && domain.status === 'active' && (
            <span>
              · {domain.autoRenew ? 'Renews' : 'Expires'}{' '}
              {new Date(domain.expiresAt).toLocaleDateString()}
              {domain.autoRenew &&
                domain.renewalPriceCredits !== null &&
                ` for ${formatCreditsWithDollar(domain.renewalPriceCredits)}`}
            </span>
          )}
          {domain.mode === 'nameservers' && domain.expiresAt && (
            <span>
              · Expires {new Date(domain.expiresAt).toLocaleDateString()} (renewed at your
              registrar)
            </span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {state.detail && <p className="text-sm">{state.detail}</p>}
        {domain.contactVerified === false && (
          <p role="status" className="text-sm">
            Check the registrant email inbox: ICANN requires the contact address to be confirmed, or
            the domain is suspended.
          </p>
        )}
        {verifiable && !showNs && (
          // Healthy: the records are not shown, but the owner can still read
          // the DNS now (the server admits an active domain for that).
          <div className="rounded-md border p-3 text-sm">
            <p className="font-medium">
              {domain.mode === 'nameservers' ? 'Name servers' : 'Delegation'}
            </p>
            <p className="text-xs text-muted-foreground">
              {domain.mode === 'nameservers'
                ? 'This domain uses Fias’s name servers.'
                : 'This name is delegated to Fias’s name servers.'}
            </p>
            <VerifyDelegation
              domain={domain}
              check={check}
              verifying={verify.isPending}
              onVerify={() => verify.mutate()}
              recordsShown={false}
            />
          </div>
        )}
        {showNs &&
          (domain.mode === 'nameservers' ? (
            // Not a DNS record: at a registrar the Company replaces a list of
            // name servers, so there is no Type to copy.
            <RecordToPublish
              title="Name servers to set at your registrar"
              name={domain.zoneName}
              values={domain.nameServers}
              note="Replace all of this domain’s name servers with exactly these. While the domain stays at your registrar, keep its account protected with two-step sign-in and a registrar lock: whoever controls it can point the domain elsewhere."
              checks={check}
              action={
                verifiable && (
                  <VerifyDelegation
                    domain={domain}
                    check={check}
                    verifying={verify.isPending}
                    onVerify={() => verify.mutate()}
                  />
                )
              }
            />
          ) : (
            <RecordToPublish
              type="NS"
              name={domain.zoneName}
              values={domain.nameServers}
              note="Add these as NS records for exactly this name, where the rest of your domain’s DNS is managed. Keep every other record as it is."
              checks={check}
              action={
                verifiable && (
                  <VerifyDelegation
                    domain={domain}
                    check={check}
                    verifying={verify.isPending}
                    onVerify={() => verify.mutate()}
                  />
                )
              }
            />
          ))}
        {domain.status === 'pending_review' && isOwner && (
          <SubmitTransfer companyId={companyId} domain={domain} onDone={onChanged} />
        )}
        {outCode && (
          <div role="status" className="rounded-md border p-3 text-sm">
            <p className="mb-2 font-medium">Transfer code</p>
            <div className="flex items-center justify-between gap-2">
              <span className="break-all font-mono text-xs">{outCode}</span>
              <Button
                size="icon"
                variant="ghost"
                aria-label="Copy transfer code"
                onClick={() => copy(outCode)}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Give this to your new registrar. Fias does not keep it — copy it now.
            </p>
          </div>
        )}
        {registeredHere && domain.autoRenew && domain.renewalFailures > 0 && (
          <div
            role="alert"
            className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-700 dark:bg-amber-950"
            data-testid="company-web-renewal-failed"
          >
            <p>
              Fias could not charge this domain’s renewal
              {domain.renewalFailures > 1 ? ` (${domain.renewalFailures} days in a row)` : ''}
              {domain.renewalLastError ? `: ${domain.renewalLastError}` : ''}. Fias tries again
              daily; if it keeps failing, renewal turns off and the domain will expire.
            </p>
          </div>
        )}
        {registeredHere &&
          domain.autoRenew &&
          typeof domain.renewalQuoteHoldCredits === 'number' && (
            <div
              className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-700 dark:bg-amber-950"
              data-testid="company-web-renewal-quote-hold"
            >
              <p>
                The registrar now charges {formatCreditsWithDollar(domain.renewalQuoteHoldCredits)}{' '}
                a year for this domain
                {domain.renewalPriceCredits !== null &&
                  `, up from ${formatCreditsWithDollar(domain.renewalPriceCredits)}`}
                . Fias has not charged it: confirm the new price to renew, or turn renewal off.
              </p>
              {isOwner && (
                <Button
                  size="sm"
                  className="mt-2"
                  disabled={confirmRenewal.isPending}
                  onClick={() => confirmRenewal.mutate()}
                >
                  Renew at the new price
                </Button>
              )}
            </div>
          )}
        {registeredHere && domain.status === 'active' && (
          <label className="flex items-center gap-2 text-sm">
            <Switch
              checked={domain.autoRenew}
              disabled={!isOwner || autoRenew.isPending}
              onCheckedChange={(on) => autoRenew.mutate(on)}
              aria-label="Renew automatically"
            />
            Renew automatically each year (charged to your Company’s payer 30 days before)
          </label>
        )}
        <div className="flex flex-wrap gap-2">
          {domain.recordsEditor && domain.status !== 'removing' && (
            <Button size="sm" variant="outline" onClick={() => setShowRecords((v) => !v)}>
              {showRecords ? 'Hide DNS records' : 'DNS records'}
            </Button>
          )}
          {isOwner &&
            registeredHere &&
            (domain.status === 'active' || domain.status === 'removing') && (
              <Button size="sm" variant="outline" onClick={() => setTransferringOut(true)}>
                Transfer out
              </Button>
            )}
          {isOwner && domain.status !== 'removing' && (
            <Button size="sm" variant="ghost" onClick={() => setRemoving(true)}>
              Stop using
            </Button>
          )}
        </div>
        {showRecords && domain.recordsEditor && (
          <RecordsEditor companyId={companyId} domain={domain} isOwner={isOwner} />
        )}
      </CardContent>

      <AlertDialog open={removing} onOpenChange={setRemoving}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Stop using {domain.zoneName}?</AlertDialogTitle>
            <AlertDialogDescription>
              Every portal on this domain stops at once and everyone signed in there is signed out.
              {registeredHere
                ? ' The domain stays yours, and charges already made are not refunded; transfer it to another registrar to take it with you.'
                : domain.mode === 'nameservers'
                  ? ' Then point its name servers away from Fias at your registrar to finish; Fias keeps answering for it until you do.'
                  : ' Then remove its NS records from your DNS to finish.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={remove.isPending} onClick={() => remove.mutate()}>
              Stop using
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={transferringOut} onOpenChange={setTransferringOut}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Transfer {domain.zoneName} out of Fias?</AlertDialogTitle>
            <AlertDialogDescription>
              Every portal on this domain stops at once. Fias unlocks the domain and shows you its
              transfer code once, for your new registrar. Its DNS records keep working until the
              transfer completes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={transferOut.isPending}
              onClick={() => transferOut.mutate()}
            >
              Unlock and show code
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

/** What each Verify state tells the Company, in their words (as for email domains). */
const NS_CHECK_LABEL: Record<
  CompanyWebDelegationCheckDto['records'][number]['state'],
  { label: string; ok: boolean }
> = {
  found: { label: 'Found', ok: true },
  missing: { label: 'Not found yet', ok: false },
  unknown: { label: 'Could not check', ok: false },
};

/** The last Verify result for one name server, if there is one. */
function nsCheckState(check: CompanyWebDelegationCheckDto | null, value: string) {
  // The server reports each name server by the very string it was asked for.
  return check?.records.find((r) => r.value === value)?.state;
}

/** Why the DNS could not be read, in the Company's words. */
const NS_CHECK_ERROR: Record<CompanyWebDelegationCheckError, string> = {
  timeout: 'The DNS did not answer in time.',
  no_parent_zone: 'Fias could not find the DNS that answers for this name.',
  no_parent_server: 'None of the name servers for this domain’s DNS could be reached.',
  bad_answer: 'This domain’s DNS gave an answer Fias could not use.',
  dns_error: 'The DNS could not be read.',
};

/**
 * The Verify button for a domain the Company points at Fias, and what the
 * last check found. The same read the reconciler makes — so "Found" for
 * every name server means Fias finishes on its own within a couple of
 * minutes; the card is already following the domain.
 */
function VerifyDelegation({
  domain,
  check,
  verifying,
  onVerify,
  recordsShown = true,
}: {
  domain: CompanyWebDomainDto;
  check: CompanyWebDelegationCheckDto | null;
  verifying: boolean;
  onVerify: () => void;
  /** False on a healthy domain, where the records are not on the card to point "above" at. */
  recordsShown?: boolean;
}) {
  // A name-server domain is pointed at a registrar, where the whole list is
  // replaced; a delegation is NS records added where the DNS is managed.
  const atRegistrar = domain.mode === 'nameservers';
  const healthy = domain.status === 'active' && domain.delegationStatus === 'delegated';
  const publish = atRegistrar
    ? recordsShown
      ? 'Set the name servers above at your registrar'
      : 'Set Fias’s name servers at your registrar'
    : recordsShown
      ? 'Add the records above where your domain’s DNS is managed'
      : 'Put the NS records for this name back where your domain’s DNS is managed';
  // What the owner does when a healthy domain's DNS turns out to have moved: the
  // reconciler marks the domain and the card then shows the records to put back.
  const drifted =
    ' Fias will mark this domain within a couple of minutes and show the name servers to put back.';
  let verdict: string | null = null;
  if (check) {
    switch (check.status) {
      case 'delegated':
        verdict =
          domain.status === 'pending_delegation'
            ? 'Every name server is in place. Fias finishes setting up within a couple of minutes.'
            : healthy
              ? 'Every name server is in place.'
              : 'Every name server is in place again. Fias picks it up within a couple of minutes.';
        break;
      case 'not_delegated':
        verdict = recordsShown
          ? `Nothing found for ${domain.zoneName} yet. ${publish}, then verify again — a change can take a few minutes to show.`
          : `Nothing found for ${domain.zoneName}.${drifted}`;
        break;
      case 'mismatch':
        verdict =
          check.unexpected.length > 0
            ? `Found name servers that are not Fias’s: ${check.unexpected.join(', ')}.${recordsShown ? ' Replace them with exactly the ones above.' : drifted}`
            : !recordsShown
              ? `Some of the name servers are missing.${drifted}`
              : atRegistrar
                ? 'Some of the name servers are missing. Set the domain’s name servers to exactly the ones above, then verify again.'
                : 'Some of the name servers are missing. Add the ones marked “Not found yet”, then verify again.';
        break;
      default:
        verdict = `${NS_CHECK_ERROR[check.error ?? 'dns_error']} Try again in a minute.`;
    }
  }
  return (
    <div className="mt-2 space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" disabled={verifying} onClick={onVerify}>
          {verifying ? 'Verifying…' : 'Verify'}
        </Button>
        <span className="text-xs text-muted-foreground">
          Checks what the DNS shows right now. Fias also checks on its own every few minutes.
        </span>
      </div>
      {verdict && (
        <p
          role="status"
          className={`text-xs ${check?.status === 'delegated' ? '' : 'text-destructive'}`}
        >
          {verdict}
        </p>
      )}
    </div>
  );
}

function RecordToPublish({
  title = 'DNS records to add',
  type,
  name,
  values,
  note,
  checks = null,
  action,
}: {
  title?: string;
  /** The record type; none where what is published is not a DNS record (a registrar's name-server list). */
  type?: string;
  name: string;
  values: string[];
  note: string;
  /** The last Verify result, shown beside each value. */
  checks?: CompanyWebDelegationCheckDto | null;
  /** Rendered under the records: the Verify button. */
  action?: ReactNode;
}) {
  return (
    <div className="rounded-md border p-3 text-sm">
      <p className="mb-2 font-medium">{title}</p>
      <dl className="space-y-1">
        {type && (
          <div className="grid grid-cols-[4rem_1fr] items-center gap-x-3">
            <dt className="text-muted-foreground">Type</dt>
            <dd>{type}</dd>
          </div>
        )}
        <div className="grid grid-cols-[4rem_1fr] items-center gap-x-3">
          <dt className="text-muted-foreground">Name</dt>
          <dd className="flex items-center justify-between gap-2">
            <span className="break-all font-mono text-xs">{name}</span>
            <Button size="icon" variant="ghost" aria-label="Copy name" onClick={() => copy(name)}>
              <Copy className="h-4 w-4" />
            </Button>
          </dd>
        </div>
        {values.map((value) => {
          const state = nsCheckState(checks, value);
          return (
            <div key={value} className="grid grid-cols-[4rem_1fr] items-center gap-x-3">
              <dt className="text-muted-foreground">Value</dt>
              <dd className="flex items-center justify-between gap-2">
                <span className="break-all font-mono text-xs">{value}</span>
                <span className="ml-auto flex items-center gap-1">
                  {state && (
                    <span
                      className={`text-xs ${NS_CHECK_LABEL[state].ok ? 'text-muted-foreground' : 'text-destructive'}`}
                    >
                      {NS_CHECK_LABEL[state].label}
                    </span>
                  )}
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label={`Copy ${value}`}
                    onClick={() => copy(value)}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </span>
              </dd>
            </div>
          );
        })}
      </dl>
      <p className="mt-2 text-xs text-muted-foreground">{note}</p>
      {action}
    </div>
  );
}

const EMPTY_CONTACT: CompanyDomainRegistrantContactDto = {
  firstName: '',
  lastName: '',
  organizationName: '',
  email: '',
  phoneNumber: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  countryCode: '',
  zipCode: '',
};

function contactComplete(c: CompanyDomainRegistrantContactDto): boolean {
  return (
    [c.firstName, c.lastName, c.organizationName, c.email, c.addressLine1, c.city, c.zipCode].every(
      (v) => v.trim().length > 0,
    ) &&
    REGISTRAR_PHONE_PATTERN.test(normalizeRegistrarPhone(c.phoneNumber, c.countryCode)) &&
    isRegistrarCountryCode(c.countryCode)
  );
}

/**
 * Drops empty optional fields, which the server's strict schema would refuse
 * as blank, and sends the phone in the registrar's shape (an autofilled
 * number the person never left the field of was not normalised on blur).
 */
function cleanContact(c: CompanyDomainRegistrantContactDto): CompanyDomainRegistrantContactDto {
  const { addressLine2, state, ...required } = c;
  return {
    ...required,
    phoneNumber: normalizeRegistrarPhone(c.phoneNumber, c.countryCode),
    ...(addressLine2?.trim() ? { addressLine2: addressLine2.trim() } : {}),
    ...(state?.trim() ? { state: state.trim() } : {}),
  };
}

/**
 * What the browser's autofill may put in each registrant field (WHATWG
 * autocomplete tokens). Naming them keeps a saved address landing in the right
 * boxes; the country is a list, so autofill picks the code rather than typing
 * a name into a two-letter box.
 */
const REGISTRANT_AUTOCOMPLETE: Partial<Record<keyof CompanyDomainRegistrantContactDto, string>> = {
  firstName: 'given-name',
  lastName: 'family-name',
  organizationName: 'organization',
  email: 'email',
  phoneNumber: 'tel',
  addressLine1: 'address-line1',
  addressLine2: 'address-line2',
  city: 'address-level2',
  state: 'address-level1',
  zipCode: 'postal-code',
};

const COUNTRY_OPTIONS = registrarCountryOptions();

function RegistrantContactFields({
  idPrefix,
  value,
  onChange,
}: {
  idPrefix: string;
  value: CompanyDomainRegistrantContactDto;
  onChange: (c: CompanyDomainRegistrantContactDto) => void;
}) {
  const field = (key: keyof CompanyDomainRegistrantContactDto, label: string, extra = {}) => (
    <div className="grid gap-1.5">
      <Label htmlFor={`${idPrefix}-${key}`}>{label}</Label>
      <Input
        id={`${idPrefix}-${key}`}
        value={value[key] ?? ''}
        autoComplete={REGISTRANT_AUTOCOMPLETE[key]}
        onChange={(e) => onChange({ ...value, [key]: e.target.value })}
        onBlur={
          key === 'phoneNumber'
            ? () =>
                onChange({
                  ...value,
                  phoneNumber: normalizeRegistrarPhone(value.phoneNumber, value.countryCode),
                })
            : undefined
        }
        {...extra}
      />
    </div>
  );
  return (
    <fieldset className="space-y-3">
      <legend className="text-sm font-medium">Registrant</legend>
      <p className="text-xs text-muted-foreground">
        Your Company is the domain’s registrant. Registries require a real contact; Fias turns on
        privacy protection so it is not published.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        {field('firstName', 'First name', { maxLength: 100 })}
        {field('lastName', 'Last name', { maxLength: 100 })}
        {field('organizationName', 'Organization', { maxLength: 200 })}
        {field('email', 'Email', { type: 'email', maxLength: 254 })}
        {field('phoneNumber', 'Phone (+1.4155550100)', { maxLength: 32 })}
        {field('addressLine1', 'Address', { maxLength: 255 })}
        {field('addressLine2', 'Address line 2 (optional)', { maxLength: 255 })}
        {field('city', 'City', { maxLength: 100 })}
        {field('state', 'State / region (optional)', { maxLength: 100 })}
        {field('zipCode', 'Postal code', { maxLength: 20 })}
        <div className="grid gap-1.5">
          <Label htmlFor={`${idPrefix}-countryCode`}>Country</Label>
          <select
            id={`${idPrefix}-countryCode`}
            className={SELECT_CLASS}
            autoComplete="country"
            value={value.countryCode}
            onChange={(e) => onChange({ ...value, countryCode: e.target.value })}
          >
            <option value="">Select a country</option>
            {COUNTRY_OPTIONS.map((o) => (
              <option key={o.code} value={o.code}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    </fieldset>
  );
}

function SubmitTransfer({
  companyId,
  domain,
  onDone,
}: {
  companyId: string;
  domain: CompanyWebDomainDto;
  onDone: () => void;
}) {
  const [authCode, setAuthCode] = useState('');
  const [contact, setContact] = useState(EMPTY_CONTACT);
  const queryClient = useQueryClient();
  const quoteKey = queryKeys.companies.webDomainTransferQuote(companyId, domain.domainId);
  // The price the transfer will charge, shown before the owner agrees to it
  // and sent back as the ceiling (a higher fresh quote charges nothing).
  const quote = useQuery({
    queryKey: quoteKey,
    queryFn: () => api.quoteCompanyWebDomainTransfer(companyId, domain.domainId),
    staleTime: 5 * 60_000,
  });
  const submit = useMutation({
    mutationFn: (expectedCredits: number) =>
      api.submitCompanyWebDomainTransfer(companyId, domain.domainId, {
        authCode: authCode.trim(),
        registrantContact: cleanContact(contact),
        expectedCredits,
      }),
    onSuccess: () => {
      setAuthCode('');
      onDone();
    },
    onError: (err) => {
      const newPrice = risenPrice(err);
      if (newPrice !== null) {
        queryClient.setQueryData(quoteKey, (old: typeof quote.data) =>
          old ? { ...old, priceCredits: newPrice } : old,
        );
        toast({
          title: 'The price went up',
          description: `The transfer now costs ${formatCreditsWithDollar(newPrice)}. Nothing was charged — check the new price and try again.`,
        });
        return;
      }
      toast({ title: 'Transfer not started', description: errorText(err), variant: 'destructive' });
    },
  });
  const price = quote.data?.priceCredits ?? null;
  const ready =
    price !== null && authCode.trim().length > 0 && contactComplete(contact) && !submit.isPending;
  return (
    <form
      className="space-y-4 rounded-md border p-3"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        if (ready && price !== null) submit.mutate(price);
      }}
    >
      <p className="text-sm font-medium">Move {domain.zoneName} to Fias</p>
      <p className="text-xs text-muted-foreground">
        Unlock the domain at your current registrar and get its transfer (auth) code. The transfer
        includes a year’s renewal, charged in credits to your Company’s payer when you submit; it is
        refunded if the transfer does not go through.
      </p>
      {price !== null && (
        <p role="status" className="text-sm" data-testid="company-web-transfer-price">
          The transfer costs {formatCreditsWithDollar(price)}
          {quote.data &&
            `, then ${formatCreditsWithDollar(quote.data.renewalPriceCredits)} a year to renew`}
          .
        </p>
      )}
      {quote.isError && (
        <p role="alert" className="text-sm text-destructive">
          Fias could not get a transfer price for {domain.zoneName}: {errorText(quote.error)}
        </p>
      )}
      <div className="grid gap-1.5">
        <Label htmlFor={`auth-${domain.domainId}`}>Transfer code</Label>
        <Input
          id={`auth-${domain.domainId}`}
          autoComplete="off"
          value={authCode}
          maxLength={256}
          onChange={(e) => setAuthCode(e.target.value)}
        />
      </div>
      <RegistrantContactFields
        idPrefix={`tr-${domain.domainId}`}
        value={contact}
        onChange={setContact}
      />
      <Button type="submit" disabled={!ready}>
        {submit.isPending
          ? 'Submitting…'
          : price !== null
            ? `Start transfer for ${formatCreditsWithDollar(price)}`
            : 'Start transfer'}
      </Button>
    </form>
  );
}

type AddMode = 'buy' | 'transfer' | 'nameservers' | 'delegate';

const ADD_MODE_LABEL: Record<AddMode, string> = {
  buy: 'Buy a new domain',
  transfer: 'Move a domain you own (most secure)',
  nameservers: 'Point your name servers to Fias',
  delegate: 'Use a subdomain',
};

function AddDomain({
  companyId,
  data,
  onAdded,
}: {
  companyId: string;
  data: CompanyWebDto;
  onAdded: () => void;
}) {
  const modes: AddMode[] = [
    ...(data.domainPurchases ? (['buy'] as const) : []),
    ...(data.approvedDomains.length > 0 ? (['transfer', 'nameservers', 'delegate'] as const) : []),
  ];
  // The choice, if still offered; otherwise the first mode offered — read every
  // render, so modes that arrive after mounting (a grant just approved) show.
  const [chosenMode, setMode] = useState<AddMode | null>(null);
  const mode = chosenMode && modes.includes(chosenMode) ? chosenMode : (modes[0] ?? null);
  if (!mode) {
    return (
      <p className="text-sm text-muted-foreground">
        Ask Fias to approve a domain you own, or to let your Company buy one, to add a portal.
      </p>
    );
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle>Add a domain</CardTitle>
        <CardDescription>
          Portals live on a domain Fias manages for you, so nobody can point their address
          elsewhere.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {modes.length > 1 && (
          <div role="radiogroup" aria-label="How to add a domain" className="flex flex-wrap gap-2">
            {modes.map((m) => (
              <Button
                key={m}
                type="button"
                size="sm"
                role="radio"
                aria-checked={mode === m}
                variant={mode === m ? 'default' : 'outline'}
                onClick={() => setMode(m)}
              >
                {ADD_MODE_LABEL[m]}
              </Button>
            ))}
          </div>
        )}
        {mode === 'buy' && <BuyDomain companyId={companyId} onAdded={onAdded} />}
        {mode === 'transfer' && (
          <ZoneRequest
            companyId={companyId}
            options={data.approvedDomains}
            label="Domain"
            help="Fias copies what this domain’s DNS shows today for you to check, then asks for its transfer code. Your email and other records keep working: you manage them in Fias afterwards."
            submitLabel="Prepare transfer"
            request={(zone) => api.startCompanyWebDomainTransfer(companyId, zone)}
            onAdded={onAdded}
          />
        )}
        {mode === 'nameservers' && (
          <ZoneRequest
            companyId={companyId}
            options={data.approvedDomains}
            label="Domain"
            help="The domain stays at your registrar, and so does renewing it. Fias copies what its DNS shows today for you to check, then shows the name servers to set. Less secure than moving it: whoever controls your registrar account can point the domain elsewhere, which stops its portals until it is put back."
            submitLabel="Prepare name servers"
            request={(zone) => api.pointCompanyWebDomainNameservers(companyId, zone)}
            onAdded={onAdded}
          />
        )}
        {mode === 'delegate' && (
          <ZoneRequest
            companyId={companyId}
            options={data.approvedDomains}
            subdomain
            label="Subdomain"
            help="Fias hosts just this name — your domain and its other records stay where they are. You add NS records for it next."
            submitLabel="Delegate subdomain"
            request={(zone) => api.delegateCompanyWebDomain(companyId, zone)}
            onAdded={onAdded}
          />
        )}
      </CardContent>
    </Card>
  );
}

function ZoneRequest({
  options,
  subdomain = false,
  label,
  help,
  submitLabel,
  request,
  onAdded,
}: {
  companyId: string;
  options: string[];
  subdomain?: boolean;
  label: string;
  help: string;
  submitLabel: string;
  request: (zoneName: string) => Promise<unknown>;
  onAdded: () => void;
}) {
  const [sub, setSub] = useState('');
  const [base, setBase] = useState(options[0] ?? '');
  const zone = subdomain ? `${sub.trim().toLowerCase()}.${base}` : base;
  const add = useMutation({
    mutationFn: () => request(zone),
    onSuccess: () => {
      setSub('');
      onAdded();
    },
    onError: (err) =>
      toast({
        title: 'Could not add the domain',
        description: errorText(err),
        variant: 'destructive',
      }),
  });
  const ready = base.length > 0 && (!subdomain || sub.trim().length > 0) && !add.isPending;
  return (
    <form
      className="space-y-3"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        if (ready) add.mutate();
      }}
    >
      <div className="grid gap-1.5">
        <Label htmlFor={subdomain ? 'zone-sub' : 'zone-base'}>{label}</Label>
        <div className="flex flex-wrap items-center gap-2">
          {subdomain && (
            <>
              <Input
                id="zone-sub"
                className="w-40"
                placeholder="portal"
                value={sub}
                maxLength={63}
                onChange={(e) => setSub(e.target.value)}
              />
              <span className="text-sm text-muted-foreground">.</span>
            </>
          )}
          <select
            id={subdomain ? undefined : 'zone-base'}
            aria-label="Approved domain"
            className={SELECT_CLASS}
            value={base}
            onChange={(e) => setBase(e.target.value)}
          >
            {options.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>
        <p className="text-xs text-muted-foreground">{help}</p>
      </div>
      <Button type="submit" disabled={!ready}>
        {add.isPending ? 'Adding…' : submitLabel}
      </Button>
    </form>
  );
}

function BuyDomain({ companyId, onAdded }: { companyId: string; onAdded: () => void }) {
  const [name, setName] = useState('');
  const [quote, setQuote] = useState<CompanyWebDomainQuoteDto | null>(null);
  const [contact, setContact] = useState(EMPTY_CONTACT);
  const search = useMutation({
    mutationFn: () => api.searchCompanyWebDomain(companyId, name.trim().toLowerCase()),
    onSuccess: setQuote,
    onError: (err) =>
      toast({
        title: 'Could not check that domain',
        description: errorText(err),
        variant: 'destructive',
      }),
  });
  const buy = useMutation({
    mutationFn: () =>
      api.purchaseCompanyWebDomain(companyId, {
        zoneName: quote!.zoneName,
        registrantContact: cleanContact(contact),
        // The price on the button: a ceiling, never the charge.
        expectedCredits: quote!.priceCredits!,
      }),
    onSuccess: () => {
      setName('');
      setQuote(null);
      onAdded();
    },
    onError: (err) => {
      const newPrice = risenPrice(err);
      if (newPrice !== null && quote) {
        // Show the new price on the button; buying it is a new click.
        setQuote({ ...quote, priceCredits: newPrice });
        toast({
          title: 'The price went up',
          description: `${quote.zoneName} now costs ${formatCreditsWithDollar(newPrice)}. Nothing was charged — check the new price and buy again.`,
        });
        return;
      }
      toast({
        title: 'Could not buy the domain',
        description: errorText(err),
        variant: 'destructive',
      });
    },
  });
  const canBuy =
    quote?.available === true &&
    quote.priceCredits !== null &&
    contactComplete(contact) &&
    !buy.isPending;
  return (
    <div className="space-y-4">
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (name.trim()) search.mutate();
        }}
      >
        <div className="grid gap-1.5">
          <Label htmlFor="buy-name">Domain</Label>
          <Input
            id="buy-name"
            className="w-64"
            placeholder="yourcompany.com"
            value={name}
            maxLength={253}
            onChange={(e) => {
              setName(e.target.value);
              setQuote(null);
            }}
          />
        </div>
        <Button type="submit" variant="outline" disabled={!name.trim() || search.isPending}>
          {search.isPending ? 'Checking…' : 'Check'}
        </Button>
      </form>
      {quote && !quote.available && (
        <p role="status" className="text-sm">
          {quote.zoneName} is not available.
        </p>
      )}
      {quote?.available && quote.priceCredits !== null && (
        <form
          className="space-y-4"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            if (canBuy) buy.mutate();
          }}
        >
          <p role="status" className="text-sm">
            <strong>{quote.zoneName}</strong> is available:{' '}
            {formatCreditsWithDollar(quote.priceCredits)} for the first year
            {quote.renewalPriceCredits !== null &&
              `, then ${formatCreditsWithDollar(quote.renewalPriceCredits)} a year`}
            . Charged to your Company’s payer when you buy; not refundable once registered.
          </p>
          <RegistrantContactFields idPrefix="buy" value={contact} onChange={setContact} />
          <Button type="submit" disabled={!canBuy}>
            {buy.isPending ? 'Buying…' : `Buy for ${formatCreditsWithDollar(quote.priceCredits)}`}
          </Button>
        </form>
      )}
    </div>
  );
}

function RecordsEditor({
  companyId,
  domain,
  isOwner,
}: {
  companyId: string;
  domain: CompanyWebDomainDto;
  isOwner: boolean;
}) {
  const qc = useQueryClient();
  const key = queryKeys.companies.webDomainRecords(companyId, domain.domainId);
  const records = useQuery({
    queryKey: key,
    queryFn: () => api.listCompanyWebDnsRecords(companyId, domain.domainId),
    refetchInterval: (q) =>
      q.state.data?.records.some((r) => r.syncStatus === 'pending' || r.syncStatus === 'deleting')
        ? 10_000
        : false,
  });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: key });
    // The email card shows how the rows Fias published for a sending domain stand.
    void qc.invalidateQueries({ queryKey: queryKeys.companies.email(companyId) });
  };
  const [editing, setEditing] = useState<CompanyWebDnsRecordDto | 'new' | null>(null);
  const [deletingEmailRow, setDeletingEmailRow] = useState<CompanyWebDnsRecordDto | null>(null);
  const remove = useMutation({
    mutationFn: (recordId: string) =>
      api.deleteCompanyWebDnsRecord(companyId, domain.domainId, recordId),
    onSuccess: refresh,
    onError: (err) =>
      toast({
        title: 'Could not delete the record',
        description: errorText(err),
        variant: 'destructive',
      }),
  });

  if (records.isLoading) {
    return (
      <p role="status" className="text-sm text-muted-foreground">
        Loading records…
      </p>
    );
  }
  if (records.isError) {
    return (
      <p role="alert" className="text-sm text-destructive">
        Could not load the records. {errorText(records.error)}
      </p>
    );
  }
  const list = records.data?.records ?? [];
  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        Everything at this domain other than its portals: mail, verification records and the like.
        Fias manages each portal’s own address records.
      </p>
      {list.length === 0 ? (
        <p className="text-sm text-muted-foreground">No records yet.</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-muted-foreground">
              <th className="py-1 pr-2 font-normal">Name</th>
              <th className="py-1 pr-2 font-normal">Type</th>
              <th className="py-1 pr-2 font-normal">Value</th>
              <th className="py-1 font-normal">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {list.map((r) => (
              <tr key={r.recordId} className="border-t align-top">
                <td className="py-1 pr-2 font-mono text-xs">
                  {r.name}
                  {r.source === 'email' && (
                    <span className="block font-sans text-muted-foreground">
                      Published by Fias for email sending
                    </span>
                  )}
                </td>
                <td className="py-1 pr-2">{r.type}</td>
                <td className="py-1 pr-2 font-mono text-xs break-all">
                  {r.values.join(', ')}
                  {r.syncStatus === 'failed' && (
                    <span className="block text-destructive">Not applied: {r.syncError}</span>
                  )}
                  {(r.syncStatus === 'pending' || r.syncStatus === 'deleting') && (
                    <span className="block text-muted-foreground">
                      {r.syncStatus === 'pending' ? 'Applying…' : 'Deleting…'}
                    </span>
                  )}
                </td>
                <td className="py-1 text-right whitespace-nowrap">
                  {isOwner && r.syncStatus !== 'deleting' && (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => setEditing(r)}>
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={remove.isPending}
                        onClick={() =>
                          r.source === 'email' ? setDeletingEmailRow(r) : remove.mutate(r.recordId)
                        }
                      >
                        Delete
                      </Button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <AlertDialog
        open={deletingEmailRow !== null}
        onOpenChange={(o) => !o && setDeletingEmailRow(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete a record your email sending depends on?</AlertDialogTitle>
            <AlertDialogDescription>
              Fias published this {deletingEmailRow?.type} record at {deletingEmailRow?.name} for a
              sending domain. Without it, mail from that domain fails its checks and stops being
              delivered. You can publish it again from the Email card.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction
              disabled={remove.isPending}
              onClick={() => {
                const r = deletingEmailRow;
                setDeletingEmailRow(null);
                if (r) remove.mutate(r.recordId);
              }}
            >
              Delete record
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {isOwner &&
        (editing ? (
          <RecordForm
            companyId={companyId}
            domain={domain}
            record={editing === 'new' ? null : editing}
            onDone={() => {
              setEditing(null);
              refresh();
            }}
            onCancel={() => setEditing(null)}
          />
        ) : (
          <Button size="sm" variant="outline" onClick={() => setEditing('new')}>
            Add record
          </Button>
        ))}
    </div>
  );
}

function RecordForm({
  companyId,
  domain,
  record,
  onDone,
  onCancel,
}: {
  companyId: string;
  domain: CompanyWebDomainDto;
  record: CompanyWebDnsRecordDto | null;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(record?.name ?? '@');
  const [type, setType] = useState<CompanyWebDnsRecordType>(record?.type ?? 'TXT');
  const [ttl, setTtl] = useState(String(record?.ttl ?? 300));
  const [values, setValues] = useState((record?.values ?? []).join('\n'));
  const save = useMutation({
    mutationFn: () =>
      api.putCompanyWebDnsRecord(companyId, domain.domainId, {
        name: name.trim() || '@',
        type,
        ttl: Number(ttl),
        values: values
          .split('\n')
          .map((v) => v.trim())
          .filter(Boolean),
      }),
    onSuccess: onDone,
    onError: (err) =>
      toast({
        title: 'Could not save the record',
        description: errorText(err),
        variant: 'destructive',
      }),
  });
  const ttlNumber = Number(ttl);
  const ready =
    values.trim().length > 0 &&
    Number.isInteger(ttlNumber) &&
    ttlNumber >= 60 &&
    ttlNumber <= 86400 &&
    !save.isPending;
  return (
    <form
      className="space-y-3 rounded-md border p-3"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        if (ready) save.mutate();
      }}
    >
      <div className="flex flex-wrap gap-3">
        <div className="grid gap-1.5">
          <Label htmlFor="rec-name">Name</Label>
          <Input
            id="rec-name"
            className="w-40"
            value={name}
            maxLength={253}
            disabled={record !== null}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="rec-type">Type</Label>
          <select
            id="rec-type"
            className={SELECT_CLASS}
            value={type}
            disabled={record !== null}
            onChange={(e) => setType(e.target.value as CompanyWebDnsRecordType)}
          >
            {RECORD_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="rec-ttl">TTL (seconds)</Label>
          <Input
            id="rec-ttl"
            className="w-28"
            inputMode="numeric"
            value={ttl}
            onChange={(e) => setTtl(e.target.value)}
          />
        </div>
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor="rec-values">Values (one per line)</Label>
        <textarea
          id="rec-values"
          className="min-h-20 rounded-md border border-input bg-background p-2 font-mono text-xs"
          value={values}
          onChange={(e) => setValues(e.target.value)}
        />
        <p className="text-xs text-muted-foreground">
          {name.trim() === '' || name.trim() === '@'
            ? domain.zoneName
            : `${name.trim()}.${domain.zoneName}`}
        </p>
      </div>
      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={!ready}>
          {save.isPending ? 'Saving…' : 'Save record'}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
